import { useCallback, useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { CircleAlert, Clock, Stethoscope, Timer, Users } from 'lucide-react';
import { KpiTile } from '@/components/ui/KpiTile';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import { useResizableSplit } from '@/hooks/useResizableWidth';
import { useTopbarContent } from '@/lib/topbarFocus';
import type { QueueItem } from '@/features/queue/types';
import { fetchAppointmentSummary, fetchDailySummary, fetchInventory, fetchPipelineStatus, fetchTodayQueue, fetchWaitTimes } from './api';
import { DashboardGreeting } from './DashboardGreeting';
import { fmtMin } from './DashCard';
import { QueueCard } from './QueueCard';
import { WorkflowCard } from './WorkflowCard';
import { WaitBreakdownCard } from './WaitBreakdownCard';
import { AppointmentsCard } from './AppointmentsCard';
import { StockAlertsCard, isExpired, isLow } from './StockAlertsCard';
import { QuickActionsCard } from './QuickActionsCard';
import { DashboardFooter } from './DashboardFooter';
import { DoctorDayView } from './DoctorDayView';
import { WaitInsightsPage } from './WaitInsightsPage';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import type { AppointmentSummary, DailySummary, InventoryItem, PipelineStatus, SummaryWindow, WaitTimes } from './types';

// The browser's calendar date, not toISOString()'s UTC one — for part of
// every day in a zone ahead of UTC (IST), toISOString() still names
// yesterday, which once made "Today's appointments" query the wrong day.
function todayLocalStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The headline wait: intake → doctor, from the visit starting until the doctor
// starts the consultation (backend opsMetrics.intakeToDoctorMinutes). The
// per-stage split is in the breakdown card and View insights.
const intakeToDoctor = (b: Record<string, number | null | undefined> | undefined) => b?.avg_intake_to_doctor_min ?? null;

const REFRESH_MS = 60000;

// Card placement (docs/design/dashboard, section F). Decided by the
// dashboard's own width (container query), not the viewport — collapsing the
// sidebar changes the room by 160px. One flex-wrap container; `order` and
// flex sizing move the cards between two arrangements without rendering
// anything twice:
//   >= 1000px: Queue | Workflow, then Wait | Appointments | Stock, each row
//      split by draggable dividers (like the Consultation screen's). By
//      default Queue spans exactly two KPI tiles and Workflow the other three:
//      a pane renders at (100% + 17px) * fraction - 17px with a 17px divider,
//      the same arithmetic as the KPI grid's 17px gap. Workflow never goes
//      below 460px, so its five stages stay on one row.
//   640-999px (the layout this screen had before): Queue | Appointments | Stock,
//      then Wait | Workflow (1.4/2.2 of the row). No dividers.
//   < 640px: one column.
// The break element forces the second line. Row spacing comes from each
// card's own bottom margin (gap-y-0) so the zero-height break adds none.
const GUTTER = 17;
const SLOT = {
  queue: 'order-1 @min-[640px]:grow-[2] @min-[640px]:basis-[360px] @min-[1000px]:grow-0 @min-[1000px]:min-w-[360px] @min-[1000px]:basis-[calc((100%_+_17px)*var(--f-queue)_-_17px)]',
  d1: '@min-[1000px]:order-2',
  flow: 'order-6 @min-[640px]:grow-[1.4] @min-[640px]:basis-0 @min-[1000px]:order-3 @min-[1000px]:grow-0 @min-[1000px]:shrink-0 @min-[1000px]:min-w-[460px] @min-[1000px]:basis-[calc((100%_+_17px)*var(--f-flow)_-_17px)]',
  brk: 'order-4',
  wait: 'order-5 @min-[640px]:grow-[0.8] @min-[640px]:basis-0 @min-[1000px]:grow-0 @min-[1000px]:min-w-[240px] @min-[1000px]:basis-[calc((100%_+_17px)*var(--f-wait)_-_17px)]',
  d2: '@min-[1000px]:order-6',
  appts: 'order-2 @min-[640px]:grow-0 @min-[640px]:basis-[270px] @min-[1000px]:order-7 @min-[1000px]:min-w-[240px] @min-[1000px]:basis-[calc((100%_+_17px)*var(--f-appt)_-_17px)]',
  d3: '@min-[1000px]:order-8',
  stock: 'order-3 @min-[640px]:grow @min-[640px]:basis-[220px] @min-[1000px]:order-9 @min-[1000px]:grow-0 @min-[1000px]:min-w-[240px] @min-[1000px]:basis-[calc((100%_+_17px)*var(--f-stock)_-_17px)]',
};

function Divider({ label, className, onMouseDown }: { label: string; className: string; onMouseDown: (e: ReactMouseEvent<HTMLElement>) => void }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      title="Drag to resize"
      onMouseDown={onMouseDown}
      className={`mb-4 hidden w-[17px] shrink-0 cursor-col-resize items-center justify-center self-stretch @min-[1000px]:flex ${className}`}
    >
      <div className="h-10 w-1 rounded-full bg-border" />
    </div>
  );
}

interface DashboardProps {
  onOpenVisit?: (visitCode: string) => void;
  /** Quick Actions and every "View all" — switches App.tsx's module, like the sidebar. */
  onNavigateToModule: (mod: string) => void;
  /** Stock alerts open Pharmacy on its Inventory tab, not the default Dispensing. */
  onOpenStock: () => void;
  /** Opens a patient's record over the current screen (the doctor's "My day"). */
  onViewPatientRecord?: (uhid: string) => void;
  /** 'wait-insights' shows the wait-time insights page in place of the Dashboard. Held by App.tsx so a visit opened from it can come back. */
  page?: 'home' | 'wait-insights';
  onPageChange?: (page: 'home' | 'wait-insights') => void;
  /** Opening a visit from the insights page — App gives that visit a link back. */
  onOpenVisitFromInsights?: (visitCode: string) => void;
}

type DashView = 'mine' | 'hospital';
const VIEW_KEY = 'hms_dashboard_view';

/**
 * The staff Dashboard, rebuilt to docs/design/dashboard/Dashboard_Wireframe.html
 * (revision 3, compact density): the greeting lives in the topbar, five KPI
 * tiles, then Queue / Workflow / Wait / Appointments / Stock, Quick Actions
 * and a footer — sized so Quick Actions stays above the fold on a 1080p
 * laptop at 125% scaling.
 */
export default function Dashboard({ onOpenVisit, onNavigateToModule, onOpenStock, onViewPatientRecord, page = 'home', onPageChange, onOpenVisitFromInsights }: DashboardProps) {
  const { user, branchVersion } = useAuth();
  const { branding } = useBranding();
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [waitTimes, setWaitTimes] = useState<WaitTimes | null>(null);
  const [appointments, setAppointments] = useState<AppointmentSummary | null>(null);
  const [flowWindow, setFlowWindow] = useState<SummaryWindow>('30d');
  const [pipeline, setPipeline] = useState<PipelineStatus | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = () => {
      Promise.all([fetchDailySummary(), fetchTodayQueue(), fetchInventory()])
        .then(([s, q, i]) => { if (alive) { setSummary(s); setQueue(q); setInventory(i); setError(''); } })
        .catch(e => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
      // Separate, so a hiccup in one of these cannot take the whole screen down.
      fetchWaitTimes().then(w => alive && setWaitTimes(w)).catch(() => alive && setWaitTimes(null));
      fetchAppointmentSummary(todayLocalStr()).then(a => alive && setAppointments(a)).catch(() => alive && setAppointments(null));
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [branchVersion]);

  useEffect(() => {
    let alive = true;
    fetchPipelineStatus(flowWindow).then(p => alive && setPipeline(p)).catch(() => alive && setPipeline(null));
    return () => { alive = false; };
  }, [flowWindow, branchVersion]);

  // Row splits, as fractions so they hold at any width; saved per user like the
  // Consultation screen's rail. Exposed to the classes above as CSS variables
  // through a ref (features/** bans inline style).
  const row2 = useResizableSplit({ key: 'hms_dashboard_row2_split', defaults: [2, 3], minPx: [360, 460], gutterPx: GUTTER });
  const row3 = useResizableSplit({ key: 'hms_dashboard_row3_split', defaults: [1, 1, 1], minPx: 240, gutterPx: GUTTER });
  const [fq, ff] = row2.fractions;
  const [fw, fa, fs] = row3.fractions;
  const splitVars = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    el.style.setProperty('--f-queue', String(fq));
    el.style.setProperty('--f-flow', String(ff));
    el.style.setProperty('--f-wait', String(fw));
    el.style.setProperty('--f-appt', String(fa));
    el.style.setProperty('--f-stock', String(fs));
  }, [fq, ff, fw, fa, fs]);

  const name = user?.full_name || 'there';
  const timezone = appointments?.timezone ?? null;
  const greeting = useMemo(() => <DashboardGreeting name={name} timezone={timezone} />, [name, timezone]);
  useTopbarContent(greeting);

  const today = todayLocalStr();
  const alerts = inventory.filter(i => isLow(i) || isExpired(i, today));
  const waitWeek = waitTimes ? intakeToDoctor(waitTimes.week) : null;
  const waitMonth = waitTimes?.month.sample_size ? intakeToDoctor(waitTimes.month) : null;

  // G7: a doctor opens on their own day; the hospital overview is one click away.
  const isDoctor = user?.role === 'Doctor';
  const [view, setViewState] = useState<DashView>(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'hospital' ? 'hospital' : 'mine'; } catch { return 'mine'; }
  });
  const setView = (v: DashView) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window */ } };
  const viewSwitch = isDoctor ? (
    <div className="mb-3 flex justify-end">
      <SegmentedControl<DashView> size="sm" value={view} onChange={setView}
        options={[{ value: 'mine', label: 'My day' }, { value: 'hospital', label: 'Hospital overview' }]} />
    </div>
  ) : null;
  if (page === 'wait-insights') {
    return <WaitInsightsPage onBack={() => onPageChange?.('home')} onOpenVisit={onOpenVisitFromInsights ?? onOpenVisit} />;
  }
  if (isDoctor && view === 'mine') {
    return <>{viewSwitch}<DoctorDayView onOpenVisit={onOpenVisit} onViewPatientRecord={onViewPatientRecord} /></>;
  }

  if (error && !summary) return <div className="p-5 text-[13px] text-danger-ink">Failed to load dashboard: {error}</div>;
  if (!summary) return <div className="p-5 text-[13px] text-ink-muted">Loading dashboard…</div>;

  return (
    <div className="fade-in">
      {viewSwitch}
      <div className="mb-[17px] grid grid-cols-1 gap-[17px] sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile layout="stacked" tone="blue" className="min-h-[142px]" icon={<Users className="h-6 w-6" />}
          value={summary.registrations} label="Today's registrations" sub="OPD patients"
          delta={summary.registrationsDelta != null ? { value: summary.registrationsDelta, label: 'vs yesterday' } : undefined} />
        <KpiTile layout="stacked" tone="orange" className="min-h-[142px]" icon={<Clock className="h-6 w-6" />}
          value={summary.waiting} label="Patients waiting" sub="In queue"
          delta={summary.waitingDelta != null ? { value: summary.waitingDelta, label: 'vs yesterday', goodWhen: 'down' } : undefined} />
        <KpiTile layout="stacked" tone="violet" className="min-h-[142px]" icon={<Timer className="h-6 w-6" />}
          value={fmtMin(waitWeek)} label="Avg. wait time" sub="Intake to doctor, last 7 days"
          footer={
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{waitMonth != null ? `This month: ${fmtMin(waitMonth)} avg` : ''}</span>
              <button type="button" onClick={() => onPageChange?.('wait-insights')}
                className="h-[18px] shrink-0 rounded-md border border-tone-violet/25 bg-tone-violet-soft px-2 text-[10.5px] font-medium text-tone-violet hover:brightness-95">
                View insights
              </button>
            </div>
          } />
        <KpiTile layout="stacked" tone="green" className="min-h-[142px]" icon={<Stethoscope className="h-6 w-6" />}
          value={summary.consultationsCompleted} label="Consultations" sub="Completed today"
          delta={summary.consultationsCompletedDelta != null ? { value: summary.consultationsCompletedDelta, label: 'vs yesterday' } : undefined} />
        <KpiTile layout="stacked" tone="red" className="min-h-[142px]" icon={<CircleAlert className="h-6 w-6" />}
          value={alerts.length} label="Stock alerts" sub="Low / expired"
          footer={
            <button type="button" onClick={onOpenStock}
              className="h-[18px] w-full rounded-md border border-danger/25 bg-danger-soft text-[10.5px] font-medium text-danger-ink hover:brightness-95">
              View alerts
            </button>
          } />
      </div>

      <div className="@container">
        <div ref={splitVars} className="flex flex-col gap-x-4 @min-[640px]:flex-row @min-[640px]:flex-wrap @min-[1000px]:gap-x-0">
          <div className={`mb-4 flex min-w-0 ${SLOT.queue}`}>
            <QueueCard queue={queue} onOpenVisit={onOpenVisit} onViewAll={() => onNavigateToModule('queue')} />
          </div>
          <Divider label="Resize Today's Queue and OPD Workflow Status" className={SLOT.d1} onMouseDown={row2.startResize(0)} />
          <div className={`mb-4 flex min-w-0 ${SLOT.flow}`}>
            <WorkflowCard status={pipeline} window={flowWindow} onWindowChange={setFlowWindow} />
          </div>
          <div aria-hidden className={`hidden h-0 basis-full @min-[640px]:block ${SLOT.brk}`} />
          <div className={`mb-4 flex min-w-0 ${SLOT.wait}`}>
            <WaitBreakdownCard waitTimes={waitTimes} />
          </div>
          <Divider label="Resize Patient Wait Time Breakdown and Today's Appointments" className={SLOT.d2} onMouseDown={row3.startResize(0)} />
          <div className={`mb-4 flex min-w-0 ${SLOT.appts}`}>
            <AppointmentsCard summary={appointments} onViewAll={() => onNavigateToModule('appointments')} />
          </div>
          <Divider label="Resize Today's Appointments and Stock Alerts" className={SLOT.d3} onMouseDown={row3.startResize(1)} />
          <div className={`mb-4 flex min-w-0 ${SLOT.stock}`}>
            <StockAlertsCard alerts={alerts} today={today} onOpenStock={onOpenStock} />
          </div>
        </div>
      </div>

      <QuickActionsCard modules={user?.modules ?? []} onNavigate={onNavigateToModule} />
      <DashboardFooter hospital={branding.hospital_name} branch={user?.active_branch?.name} />
    </div>
  );
}
